import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../apiConfig";


// Frame color mapping
export const FRAME_COLORS = {
  black: "#1a1a1a",
  white: "#f5f5f5",
  natural: "#c4a574",
  walnut: "#5c4033",
  gold: "#d4af37",
  silver: "#c0c0c0",
};

// Mat color mapping
export const MAT_COLORS = {
  white: "#ffffff",
  cream: "#f5f5dc",
  black: "#1a1a1a",
  grey: "#9e9e9e",
  none: null,
};

// Cache for default config
let cachedConfig = null;
let configFetched = false;

// Function to invalidate the cache (call after saving new config)
export const invalidateFrameConfigCache = () => {
  cachedConfig = null;
  configFetched = false;
};

export const useFrameConfig = () => {
  const [config, setConfig] = useState(cachedConfig);
  const [loading, setLoading] = useState(!configFetched);

  useEffect(() => {
    if (configFetched && cachedConfig !== null) {
      setConfig(cachedConfig);
      setLoading(false);
      return;
    }

    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/frames/default-config`);
        cachedConfig = response.data;
        configFetched = true;
        setConfig(response.data);
      } catch (error) {
        configFetched = true;
        cachedConfig = null;
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  return { config, loading };
};

// Helper to get frame colors from config
export const getFrameColors = (config) => {
  if (!config) return { frame: null, mat: null, innerMat: null };
  return {
    frame: FRAME_COLORS[config.frameColor] || FRAME_COLORS.black,
    mat: MAT_COLORS[config.matColor],
    innerMat: config.hasDoubleMat ? MAT_COLORS[config.innerMatColor] : null,
    frameWidth: config.frameWidth || 30,
    matWidth: config.matWidth || 50,
    innerMatWidth: config.innerMatWidth || 10,
  };
};

// Framed image component - displays full image with frame around it
export const FramedImage = ({ 
  src, 
  alt, 
  className = "",
  frameScale = 1,
  showFrame = true,
  imageScale = 100, // Product-specific image scale (50-150)
}) => {
  const { config, loading } = useFrameConfig();

  // Normalize imageScale to a percentage (100 = 100%, clamp between 50-150)
  const normalizedScale = Math.max(50, Math.min(150, imageScale || 100)) / 100;

  if (loading || !config || !showFrame) {
    return (
      <div className={`flex items-center justify-center w-full h-full image-protection-wrapper ${className}`}>
        <img 
          src={src} 
          alt={alt} 
          className="object-contain artwork-image"
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          style={{ 
            maxWidth: `${normalizedScale * 100}%`, 
            maxHeight: `${normalizedScale * 100}%` 
          }}
        />
      </div>
    );
  }

  const frameColor = FRAME_COLORS[config.frameColor] || FRAME_COLORS.black;
  const matColor = MAT_COLORS[config.matColor];
  const innerMatColor = MAT_COLORS[config.innerMatColor];
  
  // Scale widths for display (convert mm to px with scaling)
  const frameWidth = Math.max(3, Math.round(config.frameWidth * 0.12 * frameScale));
  const matWidth = config.matColor !== "none" ? Math.max(4, Math.round(config.matWidth * 0.12 * frameScale)) : 0;
  const innerMatWidth = config.hasDoubleMat && config.innerMatColor !== "none" 
    ? Math.max(2, Math.round(config.innerMatWidth * 0.12 * frameScale)) 
    : 0;

  return (
    <div className={`flex items-center justify-center w-full h-full image-protection-wrapper ${className}`}>
      <div 
        style={{ 
          maxWidth: `${normalizedScale * 100}%`, 
          maxHeight: `${normalizedScale * 100}%`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer Frame */}
        <div 
          style={{
            backgroundColor: frameColor,
            padding: `${frameWidth}px`,
            boxShadow: "0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
            borderRadius: "2px",
          }}
        >
          {/* Mat */}
          {matColor ? (
            <div
              style={{
                backgroundColor: matColor,
                padding: `${matWidth}px`,
              }}
            >
              {/* Inner Mat (Double Mat) */}
              {config.hasDoubleMat && innerMatColor ? (
                <div
                  style={{
                    backgroundColor: innerMatColor,
                    padding: `${innerMatWidth}px`,
                  }}
                >
                  <img 
                    src={src} 
                    alt={alt} 
                    className="block max-w-full h-auto object-contain artwork-image"
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </div>
              ) : (
                <img 
                  src={src} 
                  alt={alt} 
                  className="block max-w-full h-auto object-contain artwork-image"
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                />
              )}
            </div>
          ) : (
            <img 
              src={src} 
              alt={alt} 
              className="block max-w-full h-auto object-contain artwork-image"
              draggable="false"
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Product card with frame
export const FramedProductCard = ({ product, onClick, className = "" }) => {
  const { config } = useFrameConfig();
  const imageUrl = product.images?.[0] || "/placeholder.jpg";
  
  return (
    <div 
      className={`group cursor-pointer ${className}`}
      onClick={onClick}
    >
      <div className="aspect-square flex items-center justify-center p-4 bg-muted/30 rounded-xl overflow-hidden">
        {config ? (
          <FramedImage 
            src={imageUrl} 
            alt={product.title}
            frameScale={0.7}
            className="max-h-full transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <img 
            src={imageUrl}
            alt={product.title}
            className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
          />
        )}
      </div>
      <div className="mt-3">
        <h3 className="font-medium text-sm line-clamp-1">{product.title}</h3>
        {product.artist_name && (
          <p className="text-xs text-muted-foreground">{product.artist_name}</p>
        )}
        <p className="text-sm font-medium mt-1">
          ${product.price?.toFixed(2) || "0.00"}
        </p>
      </div>
    </div>
  );
};

export default FramedImage;
